import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ArrowLeft, ShieldCheck, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { auth } from '../lib/firebase';
import { dbService } from '../services/dbService';

const questions = [
    { id: "CPE1", section: "1", category: "Cognitive Load (Conception & Planning)", text: "I am consistently the one who anticipates what needs to be done before it becomes an emergency." },
    { id: "CPE2", section: "1", category: "Cognitive Load (Conception & Planning)", text: "I maintain the mental calendar of appointments, daily operations, logistics, and social obligations." },
    { id: "CPE3", section: "1", category: "Cognitive Load (Conception & Planning)", text: "When a task is delegated to my partner, I still carry the burden of confirming or reminding them to execute it." },
    { id: "CPE4", section: "2", category: "Adjustment Velocity (Self-Correction)", text: "I easily adapt my plans and correct course when my partner forgets their responsibilities." },
    { id: "CPE5", section: "2", category: "Adjustment Velocity (Self-Correction)", text: "It is structurally faster for me to just do the task myself rather than explain it or wait for it to be completed." },
    { id: "CPE6", section: "2", category: "Adjustment Velocity (Self-Correction)", text: "I frequently suppress my frustration and adjust to prevent conflict over uncompleted tasks." },
    { id: "CPE7", section: "3", category: "Operational Boundaries", text: "My partner often claims they are 'bad at' certain household or administrative tasks, leaving them to me." },
    { id: "CPE8", section: "3", category: "Operational Boundaries", text: "I am praised for being 'so organized,' which subtly traps me into maintaining all the systems." },
    { id: "CPE9", section: "3", category: "Operational Boundaries", text: "If I simply stop doing a specific task, it will not get done rather than being picked up by my partner." },
    { id: "CPE10", section: "4", category: "Consequence Buffering", text: "I intervene to handle consequences when my partner drops the ball (e.g., apologizing to friends, paying late fees)." },
    { id: "CPE11", section: "4", category: "Consequence Buffering", text: "I review or double-check my partner's contributions to ensure they meet an acceptable standard." },
    { id: "CPE12", section: "4", category: "Consequence Buffering", text: "I actively shield my partner from the emotional, social, or logistical fallout of their own disorganization." }
];

export default function FairPlayCPEAudit() {
    const [step, setStep] = useState<'INTRO' | 'ASSESSMENT' | 'RESULTS'>('INTRO');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, number>>({});
    const [isCompiling, setIsCompiling] = useState(false);

    const handleStart = () => setStep('ASSESSMENT');

    const handleAnswer = (value: number) => {
        const question = questions[currentQuestionIndex];
        setAnswers(prev => ({ ...prev, [question.id]: value }));
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const calculateResults = () => {
        let total = 0;
        
        questions.forEach((q) => {
            const val = answers[q.id] || 3; // Default 3 for neutral
            total += val;
        });

        // Min: 12, Max: 60
        // Ratio logic: Map score 12-60 to an over-functioning percentage.
        // E.g., if total = 60, overFunctioningRatio = 95%. If total = 12, overFunctioningRatio = 50%.
        // Range = 48. Base = 50. Multiplier = 45 / 48.
        const overFunctioningRatio = Math.round(50 + ((total - 12) / 48) * 45);
        const partnerRatio = 100 - overFunctioningRatio;
        
        let profileType = "balanced";
        let summaryText = "";
        let gapType = "Balanced Alliance";

        let systemicPattern = "";
        let rectificationProtocol = "";

        if (total >= 45) {
            profileType = "severe";
            gapType = "Severe Over-Functioning Protocol";
            summaryText = "You are carrying a disproportionate cognitive and operational load. Your competence has become weaponized against you, leading you to act as the executive manager rather than a partner.";
            systemicPattern = "Your structural matrix indicates critical relational over-functioning. You have assumed complete ownership of the Conception, Planning, and Execution (CPE) cycle. This creates a detrimental feedback loop: your partner's inaction is instantly buffered by your adjustment velocity. Consequence buffering prevents them from feeling the friction of their disorganized operations, permanently stalling their growth as an equal manager of the relationship.";
            rectificationProtocol = "Initiate immediate operational delegation. Stop shielding your partner from the logistical or social consequences of their inaction. Transfer cognitive load by handing over entire operational domains (Conception, Planning, Execution) rather than delegating micro-tasks. Let natural consequences play out.";
        } else if (total >= 30) {
            profileType = "moderate";
            gapType = "Emerging Workload Asymmetry";
            summaryText = "You show signs of significant operational buffering. You are stepping in to course-correct more often than necessary, masking your partner's actual capability.";
            systemicPattern = "While you have not entirely absorbed all operational domains, your adjustment velocity is too high. You frequently course-correct, remind, and buffer to prevent friction. This subtle over-functioning creates 'learned helplessness' and masks your partner's actual capability gaps, forcing you into a perpetual verification role.";
            rectificationProtocol = "Practice systemic restraint. When a task is dropped, allow the consequence to happen rather than rushing to fix it. Re-establish strict boundaries around planning and execution, shifting from a state of constant monitoring to one of mutual accountability.";
        } else {
            profileType = "balanced";
            summaryText = "You maintain healthy boundaries and generally operate within a balanced model, resisting the urge to over-function or buffer consequences.";
            systemicPattern = "Your diagnostic indicates a functional, structurally sound relational load map. You maintain healthy operational boundaries, resisting the urge to weaponize your own competence or buffer your partner's responsibilities. Cognitive load is equitably distributed.";
            rectificationProtocol = "Maintain this baseline. Continue utilizing transparent communication to calibrate cognitive load during high-stress periods, ensuring that the operational baseline remains equitable. Resist the urge to 'take over' during periods of temporary friction.";
        }

        return { total, profileType, gapType, summaryText, overFunctioningRatio, partnerRatio, systemicPattern, rectificationProtocol };
    };

    const handleNext = async () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setStep('RESULTS');
            setIsCompiling(true);

            // Save to DB
            try {
                const user = auth.currentUser;
                if (!user) throw new Error("User not found");

                const { gapType, summaryText } = calculateResults();

                await dbService.saveAuditResult(user.uid, {
                    auditType: 'fair_play_cpe',
                    title: 'The Relational Boundary & Over-Functioning Audit',
                    diagnostic: gapType,
                    summary: summaryText
                });
            } catch (err) {
                console.error("Failed to persist audit results:", err);
            }

            setTimeout(() => setIsCompiling(false), 3000);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen flex flex-col font-sans text-theme-text bg-theme-bg selection:bg-gold/30">
            <main className="flex-grow flex flex-col items-center justify-start w-full pt-20 pb-24">
                <AnimatePresence mode="wait">
                    {step === 'INTRO' && (
                        <motion.div
                            key="intro"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="flex flex-col items-center text-center max-w-2xl mx-auto px-6 py-12"
                        >
                            <h1 className="font-serif text-4xl md:text-5xl uppercase tracking-[0.15em] text-theme-text mb-8 leading-tight">
                                The Relational Boundary & Over-Functioning Audit
                            </h1>
                            <div className="w-16 h-[1px] bg-gold mb-8"></div>
                            <p className="text-theme-text/80 text-lg leading-relaxed mb-6 font-light uppercase tracking-widest text-[10px]">
                                A Comprehensive Diagnostic Built on the Fair Play CPE Framework
                            </p>
                            <p className="text-theme-text/60 text-sm leading-relaxed mb-12 max-w-xl mx-auto italic">
                                This diagnostic tracks the core pillars of relational over-functioning: Cognitive Load, Adjustment Velocity, Operational Boundaries, and Consequence Buffering. Reflect honestly on your daily operational patterns.
                            </p>

                            <button
                                onClick={handleStart}
                                className="group relative px-8 py-4 bg-transparent border border-gold text-theme-text uppercase tracking-widest text-sm font-medium overflow-hidden transition-all hover:text-white cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out -z-10"></div>
                                <span className="flex items-center gap-3 relative z-10 group-hover:text-theme-bg transition-colors">
                                    Commence Audit <ArrowRight className="w-4 h-4" />
                                </span>
                            </button>
                        </motion.div>
                    )}

                    {step === 'ASSESSMENT' && (
                        <motion.div
                            key="assessment"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="w-full max-w-3xl mx-auto px-6 py-12 flex flex-col items-center pt-24"
                        >
                            <div className="w-full max-w-xl h-[1px] bg-theme-border flex-grow-0 mb-12 relative">
                                <motion.div
                                    className="absolute top-0 left-0 h-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.5)]"
                                    initial={{ width: 0 }}
                                    animate={{ width: ((currentQuestionIndex) / questions.length) * 100 + '%' }}
                                    transition={{ duration: 0.5 }}
                                />
                            </div>

                            <div className="text-center mb-16 flex flex-col items-center gap-2">
                                <span className="font-serif text-gold tracking-widest uppercase text-base md:text-lg font-semibold">
                                    PILLAR {questions[currentQuestionIndex].section}: {questions[currentQuestionIndex].category}
                                </span>
                                <span className="text-xs tracking-widest text-taupe uppercase font-semibold">
                                    Strategic Node {currentQuestionIndex + 1} / {questions.length}
                                </span>
                            </div>

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentQuestionIndex}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="w-full flex flex-col items-center"
                                >
                                    <h2 className="text-2xl md:text-3xl font-light text-theme-text text-center leading-relaxed mb-16 min-h-[120px] flex items-center italic">
                                        "{questions[currentQuestionIndex].text}"
                                    </h2>

                                    <div className="w-full max-w-3xl mx-auto">
                                        <div className="flex flex-wrap justify-center sm:flex-nowrap sm:justify-between items-start gap-3 sm:gap-2 w-full">
                                            {[
                                                { val: 1, label: "Never" },
                                                { val: 2, label: "Rarely" },
                                                { val: 3, label: "Sometimes" },
                                                { val: 4, label: "Often" },
                                                { val: 5, label: "Always" }
                                            ].map(({ val, label }) => {
                                                const isSelected = answers[questions[currentQuestionIndex].id] === val;
                                                return (
                                                    <div key={val} className="flex flex-col items-center gap-3 w-1/4 sm:w-auto">
                                                        <button
                                                            onClick={() => handleAnswer(val)}
                                                            className={"relative w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center transition-all duration-300 cursor-pointer shrink-0 " + (isSelected ? "border-gold bg-gold text-theme-bg scale-110 shadow-[0_0_15px_rgba(212,175,55,0.4)]" : "border-theme-border text-theme-text hover:border-gold hover:text-gold hover:scale-110 hover:shadow-[0_0_15px_rgba(212,175,55,0.2)]")}
                                                        >
                                                            <span className="text-sm sm:text-base font-medium">{val}</span>
                                                        </button>
                                                        <span className={"text-[8px] sm:text-[10px] uppercase tracking-widest text-center leading-tight transition-colors mt-2 max-w-[80px] " + (isSelected ? "text-gold font-bold" : "text-taupe")}>
                                                            {label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="flex justify-between w-full mt-24 px-4 border-t border-theme-border pt-8">
                                            <button
                                                onClick={handlePrev}
                                                disabled={currentQuestionIndex === 0}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold " + (currentQuestionIndex === 0 ? "text-taupe/30 cursor-not-allowed" : "text-taupe hover:text-gold cursor-pointer")}
                                            >
                                                <ArrowLeft className="w-4 h-4" /> Previous
                                            </button>
                                            <button
                                                onClick={handleNext}
                                                disabled={!answers[questions[currentQuestionIndex].id]}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold px-6 py-3 border inline-flex " + (!answers[questions[currentQuestionIndex].id] ? "border-transparent text-taupe/30 cursor-not-allowed" : "border-gold text-gold hover:bg-gold hover:text-theme-bg cursor-pointer")}
                                            >
                                                {currentQuestionIndex === questions.length - 1 ? 'Compute Matrix' : 'Next Step'} <ArrowRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                    )}

                    {step === 'RESULTS' && (() => {
                        if (isCompiling) {
                            return (
                                <motion.div
                                    key="compiling"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="w-full max-w-4xl mx-auto px-6 py-24 flex flex-col items-center justify-center min-h-[50vh]"
                                >
                                    <div className="relative w-24 h-24 mb-12">
                                        <div className="absolute inset-0 border-t-2 border-l-2 border-gold rounded-full animate-[spin_2s_linear_infinite]"></div>
                                        <div className="absolute inset-2 border-r-2 border-b-2 border-taupe rounded-full animate-[spin_3s_linear_infinite_reverse]"></div>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Activity className="w-6 h-6 text-gold animate-pulse" />
                                        </div>
                                    </div>
                                    <h2 className="font-serif text-xl md:text-2xl uppercase tracking-[0.2em] text-gold mb-4 text-center">
                                        Evaluating Structural Submissions...
                                    </h2>
                                    <p className="text-theme-text/60 font-light text-sm tracking-widest animate-pulse text-center">
                                        Analyzing Fair Play variables...
                                    </p>
                                </motion.div>
                            );
                        }

                        const profile = calculateResults();
                        
                        const containerVariants = {
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: { staggerChildren: 0.2, delayChildren: 0.1 }
                            }
                        };

                        const itemVariants = {
                            hidden: { opacity: 0, y: 20 },
                            visible: { 
                                opacity: 1, y: 0, 
                                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } as any
                            }
                        };

                        return (
                            <motion.div
                                key="results"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="w-full max-w-4xl mx-auto px-6 py-12 pb-24"
                            >
                                <motion.div variants={itemVariants} className="text-center mb-16">
                                    <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Matrix Diagnostic</span>
                                    <h1 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-theme-text mt-6 mb-6">
                                        {profile.gapType}
                                    </h1>
                                    <p className="text-theme-text/80 text-sm leading-relaxed max-w-2xl mx-auto italic">
                                        {profile.summaryText}
                                    </p>
                                </motion.div>

                                <motion.div variants={itemVariants} className="bg-black/20 p-8 border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 shadow-md backdrop-blur-sm group mb-20 relative overflow-hidden">
                                     <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                                        <ShieldCheck className="h-48 w-48 text-gold" />
                                     </div>
                                     <h3 className="font-serif text-xl md:text-2xl uppercase tracking-widest text-theme-text mb-8 relative z-10">
                                         Over-Functioning Ratio
                                     </h3>

                                     <div className="relative z-10 flex flex-col space-y-6">
                                        <div className="flex justify-between items-end">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] uppercase tracking-widest text-theme-text/60 mb-2">You (Over-Functioning)</span>
                                                <span className="text-4xl font-serif text-gold">{profile.overFunctioningRatio}%</span>
                                            </div>
                                            <div className="flex flex-col text-right">
                                                <span className="text-[10px] uppercase tracking-widest text-theme-text/60 mb-2">Partner</span>
                                                <span className="text-4xl font-serif text-taupe">{profile.partnerRatio}%</span>
                                            </div>
                                        </div>

                                        <div className="w-full h-8 bg-theme-border/50 flex overflow-hidden border border-gold/20">
                                             <motion.div 
                                                initial={{ width: 0 }}
                                                animate={{ width: `${profile.overFunctioningRatio}%` }}
                                                transition={{ duration: 1.5, ease: "easeOut" }}
                                                className="h-full bg-gold/90 border-r border-theme-bg flex items-center justify-center relative overflow-hidden"
                                             >
                                                <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(255,255,255,0.05)_10px,rgba(255,255,255,0.05)_20px)]"></div>
                                             </motion.div>
                                             <motion.div 
                                                initial={{ width: '100%' }}
                                                animate={{ width: `${profile.partnerRatio}%` }}
                                                transition={{ duration: 1.5, ease: "easeOut" }}
                                                className="h-full bg-taupe/40"
                                             />
                                        </div>
                                     </div>
                                </motion.div>
                                
                                <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-12 mb-20 text-left">
                                    <div className="bg-black/20 p-8 border border-theme-border/50">
                                        <h3 className="text-xl font-serif italic text-gold mb-4">Systemic Pattern Analysis</h3>
                                        <p className="text-sm text-theme-text/80 leading-relaxed font-light">
                                            {profile.systemicPattern}
                                        </p>
                                    </div>
                                    <div className="bg-gold/5 p-8 border border-gold/30">
                                        <h3 className="text-xl font-serif italic text-gold mb-4">Strategic Rectification Protocol</h3>
                                        <p className="text-sm text-theme-text/80 leading-relaxed font-light">
                                            {profile.rectificationProtocol}
                                        </p>
                                    </div>
                                </motion.div>
                                
                                <motion.div variants={itemVariants} className="w-full h-[1px] bg-theme-border mb-20" />

                                <motion.div variants={itemVariants} className="text-center relative max-w-2xl mx-auto border border-theme-border p-12 bg-black/10">
                                    <ShieldCheck className="h-10 w-10 text-gold mx-auto mb-6" />
                                    <h3 className="font-serif text-xl md:text-2xl uppercase tracking-widest mb-4 text-theme-text">Diagnostic Captured</h3>
                                    <p className="text-theme-text/70 font-light text-sm mb-10 mx-auto leading-relaxed italic">
                                        Your Fair Play operational variables have been securely logged. Your dashboard has been updated to reflect this infrastructure baseline.
                                    </p>

                                    <div className="mt-8 text-center flex flex-col sm:flex-row gap-6 justify-center items-center">
                                        <Link to="/dashboard" className="btn-gold px-8 py-3">
                                            Return to Dashboard
                                        </Link>
                                    </div>
                                </motion.div>
                            </motion.div>
                        );
                    })()}
                </AnimatePresence>
            </main>
        </div>
    );
}
